import { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Popover,
  Stack,
  GraphNode,
  TabGroup,
  TabPanel,
  Tab,
  Grid,
  GridCell,
  TextBody,
  Toggle,
  Spacer,
  Divider,
  TextCaption,
} from '@pypestream/design-system';
import { type TranslationKeys, useTranslation } from '@pypestream/translations';
import type { TOptions } from 'i18next';
import { observer } from 'mobx-react-lite';

import { IconError } from './icon-error';
import { IconWarning } from './icon-warning';
import { IconCheck } from './icon-check';
import {
  useProStudioRoutes,
  useCurrentRoute,
  ProStudioPages,
} from '../../hooks';
import {
  NodeColumns,
  tableStore,
  ValidationMode,
  WorkflowError,
} from '../../stores/table-store';
import { downloadPifCompilerDiagnostics } from './download-pif-compiler-diagnostics';
import { usePifPanel } from './use-pif-panel';
import { useBobcatFlag } from '../../hooks/use-bobcat-flag';
import {
  buildErrorRowProps,
  ERROR_ROW_CAPTION_LINES,
  buildReachableRowIdPredicate,
  buildValidationTriggerLabel,
  resolveValidationTriggerBadge,
  VALIDATION_INCOMPLETE_FALLBACK,
  partitionPopoverErrors,
  type TranslateFn,
} from './validation-popover.utils';
import type { AgentCompilerMode } from '../../utils/agent-compiler-mode';
import { ErrorList, type WindowedRowProps } from './error-list';

import './validation-popover.scss';

interface ValidationPopoverProps {
  isLoading: boolean;
  errors: WorkflowError[];
  agentCompilerMode: AgentCompilerMode;
  isViewer: boolean;
  /**
   * PSTUDIO-4242: bumped when a Publish click queued behind a validation pass
   * found the draft unpublishable once the pass ended. Each new value opens
   * the popover on its errors tab, so the click is answered instead of lost.
   */
  openErrorsRequest?: number;
}

export const ValidationPopover: FC<ValidationPopoverProps> = observer(
  ({ errors, isLoading, agentCompilerMode, isViewer, openErrorsRequest }) => {
    const [popoverActive, setPopoverActive] = useState(false);
    const [popoverTab, setPopoverTab] = useState<string>();
    const { workflows } = useProStudioRoutes();
    const currentRoute = useCurrentRoute();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // The helpers in `validation-popover.utils.ts` take their strings as
    // arguments rather than importing i18next, so that module stays unit
    // testable (this component is not — it pulls in Lit and crashes the node
    // environment). This adapter is what hands them over. The `defaultValue`s
    // are a fallback for a locale that is missing the key, never the source of
    // truth: every key below exists in all five locale files.
    //
    // The two casts are what an adapter is FOR, and they are confined to it.
    // `TranslationKeys` is generated from the locale files, so a runtime-built
    // key string is not in that union; and i18next 22 types `t` as possibly
    // returning a detailed result object, which this call site never asks for
    // (no `returnObjects`, no `returnDetails`).
    const translate = useCallback<TranslateFn>(
      (key, options) =>
        t(key as TranslationKeys, options as TOptions) as string,
      [t]
    );
    const unreachableHint = translate(
      'pro-studio/navigation:validation_popover.error_row.hidden_row_hint',
      {
        defaultValue:
          'This row is hidden — clear the search or expand its group to open it.',
      }
    );
    const unknownErrorText = translate(
      'pro-studio/navigation:validation_popover.error_row.unknown_error',
      { defaultValue: 'Unknown validation error' }
    );
    const { getDiagnostics, dataForCompiling, pifErrors, getRows } =
      usePifPanel();
    const isWorkflowsPage = currentRoute.label === ProStudioPages.WORKFLOWS;
    const errorsAmount = errors.length || 0;
    const pifErrorsAmount = pifErrors.length || 0;

    const showPif = agentCompilerMode !== 'bot_only';
    const showApi =
      agentCompilerMode === 'on' || agentCompilerMode === 'bot_only';
    const sideBySide = agentCompilerMode === 'on';

    const handleTabChange = (tab: string) => {
      if (popoverActive && popoverTab === tab) {
        handlePopoverClose();
        return;
      }

      setPopoverTab(tab);

      // No imperative tab activation here (PSTUDIO-3990 review defect 3). It
      // used to call `tabsRef.current.show(tab)`, which could NEVER run: the
      // `<TabGroup>` lives behind the `popoverActive && (…)` gate below, so
      // while the popover is closed it is not mounted and the ref is null —
      // and this is the only path that opens it. Worse, both columns wrote to
      // that one ref, so in the side-by-side layout the second overwrote the
      // first and one column could not have been driven at all. The panel is
      // now activated DECLARATIVELY (`active` on the first `<Tab>` and on its
      // `<TabPanel>`), which is synchronous on mount instead of relying on
      // `ps-tab-group`'s `IntersectionObserver` (`tab-group.wc.ts`
      // `connectedCallback`) — an undocumented detail that also cannot fire
      // for a zero-sized container.
      setPopoverActive(true);
    };

    const handlePopoverClose = () => {
      setPopoverActive(false);
      setPopoverTab('');
    };

    useEffect(() => {
      if (isLoading) {
        handlePopoverClose();
      }
    }, [isLoading]);

    // Same state `handleTabChange('errors')` sets on a closed popover, set
    // directly rather than through it: that handler TOGGLES, so a request
    // arriving while the errors tab is already open would close it. A request
    // that arrives while loading waits for it to end (the effect above would
    // close it again), and is answered once — the ref stops a later loading
    // cycle from reopening a popover the user has since closed.
    const answeredOpenErrorsRequest = useRef(openErrorsRequest);
    useEffect(() => {
      if (isLoading) return;
      if (openErrorsRequest === answeredOpenErrorsRequest.current) return;
      answeredOpenErrorsRequest.current = openErrorsRequest;
      setPopoverTab('errors');
      setPopoverActive(true);
    }, [openErrorsRequest, isLoading]);

    const pifClean = pifErrorsAmount === 0;
    const apiClean = errorsAmount === 0;
    // PSTUDIO-4120: an empty list has always been read here as "clean", which is
    // only true when a pass actually produced that list. When it did not — it
    // threw, the compiler answered with its own crash, or the payload held a
    // node the grammar cannot read — the same emptiness means "nobody checked",
    // and the green tick over it is the claim QA saw next to an unpublishable
    // solution. The store labels that case; this reads the label.
    const validationIncomplete = tableStore.validationFailure !== null;
    const compiledClean =
      agentCompilerMode === 'on'
        ? pifClean && apiClean
        : agentCompilerMode === 'bot_only'
          ? apiClean
          : pifClean;
    const successfullyCompiled = compiledClean && !validationIncomplete;
    // Project Bobcat: in Manual validation, edits since the last pass hold
    // Publish until Validate runs, so "ready to be published" would be untrue.
    // Behind the master only: with it off the popover reads as in production.
    const fiveStageNavOn = useBobcatFlag('fiveStageNav') === 'allowed';
    const awaitingValidate =
      fiveStageNavOn &&
      tableStore.validationMode === ValidationMode.Manual &&
      tableStore.hasUnvalidatedChangesInManualMode;

    // PSTUDIO-3990 review defect 5: the derived node-number error is added to
    // BOTH `effectiveErrors` and `effectivePifErrors`, and with
    // `agentCompilerMode === 'on'` both columns render — so one blank cell gave
    // a badge of 2 and two identical entries. Deduped for DISPLAY only: the
    // store's eight error indexes still read the full per-list errors, so both
    // conflicting cells stay highlighted.
    //
    // Memoised on the two list identities: `effectivePifErrors` is a MobX
    // computed (stable reference until it recomputes) and `errors` is a prop,
    // so the keys are built once per real change rather than on every
    // primary-nav render — which matters precisely in the mass error state this
    // guards.
    const {
      pif: pifErrorsToRender,
      api: apiErrorsToRender,
      total: triggerBadgeCount,
    } = useMemo(
      () =>
        partitionPopoverErrors({
          pifErrors,
          apiErrors: errors,
          showPif,
          showApi,
        }),
      [pifErrors, errors, showPif, showApi]
    );

    // PSTUDIO-4120: which glyph / variant / count the trigger shows is decided
    // by a pure helper so both new branches are unit-testable — the states this
    // ticket adds (a degraded or thrown pass on a solution whose lists are
    // EMPTY) are exactly the ones no e2e can produce.
    const triggerBadge = resolveValidationTriggerBadge({
      compiledClean,
      validationIncomplete,
      badgeCount: triggerBadgeCount,
    });

    // Navigate ID-FIRST when the error carries one (PSTUDIO-3991's addressing
    // rule, now honoured by every node-number error — PSTUDIO-3990). `rowIndex`
    // is a position in the store's insertion-ordered `rows`, while both
    // `scrollToAndFocusCell` and the pending-scroll effect index
    // `getVisibleRows()` — sorted by `order`, minus hidden rows, minus rows the
    // search box filtered out. Those two agree only on an untouched table, so
    // after a drag-reorder, inside a collapsed group, or while a search is
    // active the index sends the user to a DIFFERENT row. The id-keyed pair
    // (`scrollToAndFocusCellById` / `setPendingScrollToCellByRowId`) resolves
    // the position against that same visible list, which is what makes the
    // click land on the offending cell; `primary-nav` already navigates this
    // way. The index remains the fallback for server errors that carry no id.
    const handleErrorClick = useCallback(
      ({
        columnId,
        rowIndex,
        rowId,
      }: {
        columnId: NodeColumns;
        rowIndex: number;
        rowId: string | null;
      }) => {
        // On the canvas, an error in an agent's node opens there, not in
        // the table (Edan's review, Oct 27).
        if (rowId && tableStore.showNodeOnCanvas(rowId)) {
          if (!isWorkflowsPage) navigate(workflows.path);
          handlePopoverClose();
          return;
        }
        if (!isWorkflowsPage) {
          if (rowId) {
            tableStore.setPendingScrollToCellByRowId({ rowId, columnId });
          } else {
            tableStore.setPendingScrollToCell({ rowIndex, columnId });
          }
          navigate(workflows.path);
          handlePopoverClose();
          return;
        }
        // When the error carries an id, that id is the ONLY trustworthy address.
        // `scrollToAndFocusCellById` returns false when the row is not in the
        // visible list — collapsed group, `hidden`, or filtered out by an active
        // search. Falling back to `rowIndex` there would be worse than doing
        // nothing: `rowIndex` is a position in the insertion-ordered `rows`,
        // while `scrollToAndFocusCell` indexes `getVisibleRows()`, so the same
        // number means a DIFFERENT row and the user is silently sent to an
        // unrelated cell. So the index is used only for errors that have no id
        // at all (server errors).
        //
        // A `false` here is now the RACE, not the ordinary case: an entry whose
        // row is unreachable is rendered disabled, with the reason in its
        // caption, and has no click handler at all (PSTUDIO-3990 review defect
        // 2) — a click that silently did nothing was indistinguishable from a
        // broken app for a keyboard or screen-reader user. What is left is the
        // window between the render and the click (a coworker merge, a search
        // typed in another pane); the popover stays OPEN, which is at least
        // visible as "nothing happened".
        if (rowId) {
          if (tableStore.focusCellInTable(rowId, columnId)) {
            handlePopoverClose();
          }
          return;
        }
        if (tableStore.isTableMounted) {
          tableStore.scrollToAndFocusCell(rowIndex, columnId);
        } else {
          // Build is on the canvas: ask for the table, which scrolls on mount.
          tableStore.setPendingScrollToCell({ rowIndex, columnId });
        }
        handlePopoverClose();
      },
      [isWorkflowsPage, navigate, workflows.path]
    );

    const handleValidationModeChange = useCallback(() => {
      tableStore.setValidationMode(
        tableStore.validationMode === ValidationMode.Auto
          ? ValidationMode.Manual
          : ValidationMode.Auto
      );
    }, []);

    const renderErrorRow = (params: {
      error: WorkflowError;
      idx: number;
      keyPrefix: string;
      resolveRowIndex: (e: WorkflowError) => number | null;
      isRowReachable: (rowId: string) => boolean;
      displayNumberById: ReadonlyMap<string, number>;
      windowed?: WindowedRowProps;
    }) => {
      const {
        error,
        idx,
        keyPrefix,
        resolveRowIndex,
        isRowReachable,
        displayNumberById,
        windowed,
      } = params;
      const { colId, cellNumber, nodeNumber, nodeId } = error;
      const {
        label,
        caption,
        accessibleName,
        rowNumber,
        rowIndex,
        rowId,
        state,
      } = buildErrorRowProps(error, {
        resolveRowIndex,
        displayNumberById,
        isRowReachable,
        unreachableHint,
        unknownErrorText,
        // `|| undefined`: `t` returns `string | null` under `returnNull`, and
        // undefined lets `buildErrorLabel`'s own default apply.
        solutionErrorLabel:
          t('pro-studio/navigation:vocabulary.solution_error', {
            defaultValue: 'Solution error',
          }) || undefined,
      });
      const isClickable = state === 'clickable';

      return (
        <GraphNode
          test-id={`ps-validation-popover-graph-node-${keyPrefix}-${idx}`}
          key={
            windowed?.key ??
            `${keyPrefix}-${cellNumber ?? 'n'}-${nodeId ?? nodeNumber ?? rowNumber ?? 'r'}-${idx}`
          }
          style={windowed?.style}
          aria-setsize={windowed?.setSize}
          aria-posinset={windowed?.posInSet}
          // ALWAYS a menuitem, even when it cannot be activated. Dropping the
          // role removed the entry from the menu altogether: `ps-menu` skips
          // it, a screen-reader user never hears it, and the reason it is
          // unavailable — which this PR went out of its way to state — goes
          // with it. `disabled` now reflects `aria-disabled` on the host
          // (`graph-node.wc.ts`), which is what APG asks for: a disabled menu
          // item stays present and announced, it just cannot be activated.
          role="menuitem"
          disabled={!isClickable}
          variant="flush"
          // Clamp the caption ONLY while the row is windowed. A windowed row is
          // pinned to `ERROR_ROW_ESTIMATE`, so its caption has to fit a known
          // number of lines or the pin either clips text with no signal or
          // leaves a dead strip under a short one. An UNwindowed row is not
          // pinned, the popover has room, and clamping it there would take text
          // away from the ordinary case — every real solution has a handful of
          // errors — to solve a problem it does not have (PSTUDIO-4119).
          captionLines={windowed ? ERROR_ROW_CAPTION_LINES : undefined}
          status="error"
          aria-label={accessibleName}
          // The full text as a native tooltip, since the title renders in
          // `<ps-text-body truncate>` and a long column name still pushes the
          // tail past the ellipsis. An EXTRA, not the fix: the row address
          // leads the title precisely so it survives truncation without a
          // hover (PSTUDIO-3990 review defect 1). `aria-label` wins for AT, so
          // this is not announced twice.
          title={accessibleName}
          label={label}
          caption={caption}
          nodeId={cellNumber ? String(cellNumber) : undefined}
          onClick={
            isClickable
              ? () =>
                  handleErrorClick({
                    columnId: colId as NodeColumns,
                    rowIndex: rowIndex ?? 0,
                    rowId,
                  })
              : undefined
          }
        />
      );
    };

    // Supplies the fallback index for an error with no id; the click itself
    // navigates id-first (see `handleErrorClick`). Still resolved from the id
    // where possible, so an error whose stored `rowIndex` is stale is not
    // reported as unaddressed. Whether the row can be REACHED is a separate
    // question, answered by `buildReachableRowIdPredicate` — this index cannot
    // answer it, because `visibleRowIndexById` knows nothing about an active
    // search.
    const resolveRowIndexIdFirst = (e: WorkflowError) => {
      if (e.nodeId) {
        // Same reason as the display number above: the precomputed map, not the
        // `computedFn` getter. On `main` this column read the plain
        // `e.rowIndex`, so the per-row store access is NEW here and its cost is
        // paid once per rendered row.
        const byId = tableStore.visibleRowIndexById.get(e.nodeId) ?? -1;
        if (byId !== -1) return byId;
      }
      return e.rowIndex;
    };
    const resolveApiRowIndex = resolveRowIndexIdFirst;
    const resolvePifRowIndex = (e: WorkflowError) => {
      const byIdOrIndex = resolveRowIndexIdFirst(e);
      if (byIdOrIndex != null) return byIdOrIndex;
      // Last resort: find the row by its node NUMBER. Guarded on a non-nil
      // number — `find(row.nodeNumber === null)` matches the first GROUP
      // HEADER, which holds `nodeNumber: null` by design, so an unaddressed
      // error would send the user to an unrelated group row (PSTUDIO-3990).
      if (e.nodeNumber == null) return null;
      return tableStore.getSortedRowIndex(
        tableStore
          .getAllSortedRows()
          .find((row) => row.nodeNumber === e.nodeNumber)?.id || ''
      );
    };

    const renderPifColumn = (
      isRowReachable: (rowId: string) => boolean,
      displayNumberById: ReadonlyMap<string, number>
    ) => (
      <GridCell xsmall="12" large={sideBySide ? '6' : '12'}>
        <TabGroup variant="flush">
          <Tab
            slot="tab"
            panel="errors"
            active
            onClick={() => {
              setPopoverTab('errors');
            }}
          >
            {sideBySide
              ? translate(
                  'pro-studio/navigation:validation_popover.tab.agent_validator',
                  {
                    count: pifErrorsToRender.length,
                    defaultValue: 'Agent Validator {{count}}',
                  }
                )
              : translate(
                  'pro-studio/navigation:validation_popover.tab.errors',
                  {
                    count: pifErrorsToRender.length,
                    defaultValue: 'Errors {{count}}',
                  }
                )}
          </Tab>
          {sideBySide && (
            <Tab
              slot="tab"
              panel="errors"
              onClick={() => {
                downloadPifCompilerDiagnostics(
                  getDiagnostics(),
                  dataForCompiling,
                  getRows()
                );
              }}
            >
              {translate(
                'pro-studio/navigation:validation_popover.tab.download_diagnostics',
                { defaultValue: 'Download Agent Compiler Data' }
              )}
            </Tab>
          )}
          <TabPanel
            name="errors"
            active
            style={{ marginInline: sideBySide ? 0 : -16 }}
          >
            <ErrorList
              errors={pifErrorsToRender}
              testId="ps-validation-popover-list-pif"
              renderRow={(error, idx, windowed) =>
                renderErrorRow({
                  error,
                  idx,
                  keyPrefix: 'pif',
                  resolveRowIndex: resolvePifRowIndex,
                  isRowReachable,
                  displayNumberById,
                  windowed,
                })
              }
            />
          </TabPanel>
        </TabGroup>
      </GridCell>
    );

    const renderApiColumn = (
      isRowReachable: (rowId: string) => boolean,
      displayNumberById: ReadonlyMap<string, number>
    ) => (
      <GridCell xsmall="12" large={sideBySide ? '6' : '12'}>
        <TabGroup variant="flush">
          <Tab
            slot="tab"
            panel="errors"
            active
            onClick={() => {
              setPopoverTab('errors');
            }}
          >
            {sideBySide
              ? translate(
                  'pro-studio/navigation:validation_popover.tab.bot_compiler',
                  {
                    count: apiErrorsToRender.length,
                    defaultValue: 'Bot Compiler {{count}}',
                  }
                )
              : translate(
                  'pro-studio/navigation:validation_popover.tab.errors',
                  {
                    count: apiErrorsToRender.length,
                    defaultValue: 'Errors {{count}}',
                  }
                )}
          </Tab>
          <TabPanel
            name="errors"
            active
            style={{ marginInline: sideBySide ? 0 : -16 }}
          >
            <ErrorList
              errors={apiErrorsToRender}
              testId="ps-validation-popover-list-api"
              renderRow={(error, idx, windowed) =>
                renderErrorRow({
                  error,
                  idx,
                  keyPrefix: 'api',
                  resolveRowIndex: resolveApiRowIndex,
                  isRowReachable,
                  displayNumberById,
                  windowed,
                })
              }
            />
          </TabPanel>
        </TabGroup>
      </GridCell>
    );

    // Built ONLY while the popover is open, for the same reason the columns
    // are: reading `visibleRowIndexById` / `searchResultIds` unconditionally
    // would make an always-mounted observer depend on the sorted row list and
    // on every search keystroke.
    const renderErrorColumns = () => {
      // Read ONCE here, not per row inside `renderErrorRow`.
      //
      // `ErrorList` is a plain component, so its render — and therefore
      // `renderErrorRow` — runs after this observer's reaction has closed.
      // MobX does not memoise a computed with no observers: every read of
      // `displayNumberById` there re-ran `buildDisplayNumberMap` over the whole
      // sorted row list and allocated a fresh Map. On a list of 150 entries
      // opened from a page where the table is NOT mounted (nothing else
      // observes the map) that is 150 full passes. Reading it here keeps the
      // one property this file cares about — a CLOSED popover registers no
      // dependency on the row list, because `renderErrorColumns` only runs
      // behind the `popoverActive &&` gate.
      const { displayNumberById } = tableStore;
      const isRowReachable = buildReachableRowIdPredicate({
        visibleRowIndexById: tableStore.visibleRowIndexById,
        searchResultIds: tableStore.searchQuery
          ? tableStore.searchResultIds
          : null,
      });

      return (
        <Grid>
          {showPif && renderPifColumn(isRowReachable, displayNumberById)}
          {showApi && renderApiColumn(isRowReachable, displayNumberById)}
        </Grid>
      );
    };

    return (
      <>
        <Stack gutter="xsmall" nowrap id="error-warning-popover-trigger">
          {isLoading ? (
            <span className="c-nav-validation-popover-spinner" />
          ) : (
            <Button
              test-id="ps-validation-popover-trigger"
              size="small"
              className="c-nav-validation-popover-trigger"
              variant={triggerBadge.variant}
              selected={popoverTab === 'errors'}
              // PSTUDIO-3990 review defect 7. The trigger's only content is
              // the badge NUMBER — and in the clean state a bare `<svg>` with
              // no role or label, i.e. no accessible name at all. `ps-button`
              // exposes these three properties (`button.wc.ts`), so the app can
              // name the control without touching the design system.
              // `pressed`/`aria-pressed` is deliberately NOT set: `expanded`
              // already expresses this trigger's state.
              label={buildValidationTriggerLabel(
                successfullyCompiled ? 0 : triggerBadgeCount,
                translate,
                { incomplete: validationIncomplete }
              )}
              expanded={popoverActive}
              hasPopup="menu"
              onClick={() => handleTabChange('errors')}
            >
              {triggerBadge.glyph === 'check' ? (
                <div style={{ margin: -2 }}>
                  <IconCheck testId="ps-validation-popover-icon-check" />
                </div>
              ) : (
                <div slot="prefix">
                  {/* An incomplete pass is not the same claim as N errors —
                      the warning glyph says "unknown", the error glyph says
                      "known and broken" (PSTUDIO-4120). */}
                  {triggerBadge.glyph === 'warning' ? (
                    <IconWarning testId="ps-validation-popover-icon-warning" />
                  ) : (
                    <IconError />
                  )}
                </div>
              )}
              {triggerBadge.count}
            </Button>
          )}
        </Stack>
        <Popover
          size="medium"
          width={sideBySide ? 'auto' : 'default'}
          test-id="ps-validation-popover"
          id="error-popover"
          active={popoverActive}
          trigger="error-warning-popover-trigger"
          disableTriggerListeners
          onAutoClose={handlePopoverClose}
        >
          <div style={{ maxWidth: sideBySide ? '760px' : '430px' }}>
            {!isViewer && (
              <>
                <Stack gutter="xsmall" alignItems="center" justifyContent="end">
                  <TextBody size="small">
                    {translate(
                      'pro-studio/navigation:validation_popover.auto_validation_toggle_label',
                      { defaultValue: 'Automatic validation' }
                    )}
                  </TextBody>
                  <Toggle
                    size="small"
                    test-id="ps-validation-mode-toggle"
                    checked={tableStore.validationMode === ValidationMode.Auto}
                    onChange={handleValidationModeChange}
                    disabled={isLoading}
                  />
                </Stack>
                <Spacer size="small" />
                <div style={{ marginInline: -16 }}>
                  <Divider />
                </div>
              </>
            )}

            <Spacer />
            {/* PSTUDIO-4120. Shown ABOVE whatever else the popover has to say,
                because it changes how the rest of the panel should be read: the
                list below is the last verdict, not this one. With an empty list
                it is the only thing here — there is no verdict to render. */}
            {validationIncomplete && (
              <>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  gutter="small"
                  test-id="ps-validation-popover-incomplete"
                >
                  <IconWarning />
                  <TextCaption>
                    {translate(
                      'pro-studio/navigation:validation_popover.incomplete_state',
                      { defaultValue: VALIDATION_INCOMPLETE_FALLBACK }
                    )}
                  </TextCaption>
                </Stack>
                <Spacer />
              </>
            )}
            {successfullyCompiled && awaitingValidate ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                gutter="small"
                test-id="ps-validation-popover-awaiting-validate"
              >
                <IconWarning />
                <TextCaption>
                  You&apos;ve changed the solution since the last check. Click
                  Validate before you publish.
                </TextCaption>
              </Stack>
            ) : successfullyCompiled ? (
              <Stack alignItems="center" justifyContent="center" gutter="small">
                <IconCheck />
                <TextCaption>
                  {translate(
                    'pro-studio/navigation:validation_popover.empty_state',
                    {
                      defaultValue:
                        'There are no errors, your solution is ready to be published.',
                    }
                  )}
                </TextCaption>
              </Stack>
            ) : validationIncomplete && triggerBadgeCount === 0 ? null : (
              // Built ONLY while the popover is open. These lists are plain
              // expressions evaluated during render, and `active` on <Popover>
              // controls visibility, not mounting — so without this gate every
              // error row is constructed (and its store reads registered as
              // MobX dependencies of this always-mounted observer) on every
              // render, open or not. That silently re-created the dependency on
              // row `order` that this ticket removed from the derivation, and
              // mounted thousands of <GraphNode> elements the user never asked
              // to see.
              popoverActive && renderErrorColumns()
            )}
          </div>
        </Popover>
      </>
    );
  }
);
